/**
 * Build a nullability-relaxed variant of a zod schema, preserving every effect
 * (AMP-593).
 *
 * The read boundary parses each response twice when the strict pass fails: once
 * strictly, then once through the schema this module derives. The relaxed
 * variant accepts `null`/`undefined` wherever the strict schema required a
 * value, so the nullability gaps we are still discovering in production (a
 * deleted user, a photo attachment, a portfolio-less property) degrade to a
 * null field instead of a thrown read.
 *
 * Two properties make this safe, and both are load-bearing:
 *
 * 1. **Effects are preserved.** Coercions and transforms live in `ZodEffects`
 *    (`z.preprocess`/`z.refine`) and in the `coerce` flag on primitives. The
 *    relaxed variant rebuilds those nodes rather than replacing them, so a DRF
 *    decimal string still lands as a `number` on the failure path. Returning
 *    raw data instead would hand `proposedAmount: '1234.56'` to a
 *    `number`-typed consumer — silent wrong money, exactly correlated with the
 *    failures we are hunting.
 *
 * 2. **Type-correctness is NOT relaxed.** Only nullability and presence are
 *    widened. A genuinely wrong type (a string where the schema wants an
 *    object) still fails, so it is distinguishable from a nullability gap and
 *    surfaces at higher severity.
 *
 * Nodes this cannot safely rebuild are returned unchanged; the relaxed pass
 * then simply behaves like the strict one and the caller falls through to its
 * last-resort tier. Failing to relax is always safe — relaxing something wrong
 * is not.
 */
import { z } from 'zod';

/**
 * Rebuilt schemas are cached against the schema object they derive from. The
 * boundary calls this on every response, and the module-scope schemas it parses
 * against are stable singletons, so the tree is rebuilt at most once each.
 */
const cache = new WeakMap<z.ZodTypeAny, z.ZodTypeAny>();

/**
 * Depth cap for the recursive rebuild. Deep enough for the real read schemas
 * (the deepest, `contractSchema`, nests ~6 levels through rateSets → rates);
 * shallow enough to terminate on a schema that is cyclic other than through the
 * `ZodLazy` boundary that normally breaks cycles.
 */
const MAX_DEPTH = 24;

type Def = { typeName: z.ZodFirstPartyTypeKind };

function typeNameOf(schema: z.ZodTypeAny): z.ZodFirstPartyTypeKind | undefined {
  return (schema?._def as Def | undefined)?.typeName;
}

/**
 * Widen a single object field so an absent or null value is accepted.
 *
 * `ZodDefault` is special-cased: `.nullish()` wraps the default in a
 * `ZodOptional`, which short-circuits on `undefined` BEFORE the default can
 * apply, so an omitted `attachments: z.array(...).default([])` would relax to
 * `undefined` and crash the consumer's `.map()`. Widening inside the default
 * keeps `undefined → []` while still tolerating an explicit null.
 *
 * `defaultValue` is passed through as the factory zod normalised it into,
 * NOT called here: calling it would evaluate the default once at rebuild time
 * and pin every later parse to that one value, so `.default(() => new Date())`
 * would freeze.
 */
function relaxField(schema: z.ZodTypeAny, depth: number): z.ZodTypeAny {
  if (typeNameOf(schema) === z.ZodFirstPartyTypeKind.ZodDefault) {
    const def = schema._def as unknown as {
      innerType: z.ZodTypeAny;
      defaultValue: () => unknown;
    };
    return relax(def.innerType, depth).nullable().default(def.defaultValue);
  }

  return relax(schema, depth).nullish();
}

function relaxObject(schema: z.ZodObject<z.ZodRawShape>, depth: number): z.ZodTypeAny {
  const def = schema._def;
  const shape = def.shape();
  const relaxedShape: z.ZodRawShape = {};
  for (const key of Object.keys(shape)) {
    relaxedShape[key] = relaxField(shape[key], depth + 1);
  }

  const base = z.object(relaxedShape);

  // `.strict()` is deliberately RELAXED to strip. An extra key the server
  // started sending is additive drift — neither a nullability gap nor a type
  // error — so it has no business deciding the tier. If the relaxed variant kept
  // rejecting it, a `.strict()` schema would fall all the way to the raw tier on
  // a benign server addition, and the raw tier returns the body UNPARSED: every
  // coercion that schema declares (`z.coerce`, `decimalNumber`, the `'' → null`
  // money preprocess) would be silently lost. Tier 1 still fails and still
  // reports the unknown key; tier 2 absorbs it with coercions intact.
  //
  // No `.strict()` read schema is wired to the boundary today, so this is
  // insurance rather than a live fix — but it is the cheap direction to be wrong
  // in, and the expensive one is silent uncoerced money.
  //
  // `passthrough` is preserved as-is: it never rejects, and dropping it would
  // strip keys the caller expects to survive.
  let rebuilt: z.ZodTypeAny = def.unknownKeys === 'passthrough' ? base.passthrough() : base;

  if (def.catchall && typeNameOf(def.catchall) !== z.ZodFirstPartyTypeKind.ZodNever) {
    rebuilt = (rebuilt as z.ZodObject<z.ZodRawShape>).catchall(relax(def.catchall, depth + 1));
  }

  return rebuilt;
}

/**
 * A discriminated union can only dispatch on a discriminator that still reads a
 * concrete value, so that one key keeps its strict schema while every other
 * field in each arm is widened. Relaxing it would leave zod unable to pick an
 * arm at all.
 */
function relaxDiscriminatedUnion(
  schema: z.ZodDiscriminatedUnion<string, z.ZodDiscriminatedUnionOption<string>[]>,
  depth: number,
): z.ZodTypeAny {
  const def = schema._def;
  const discriminator = def.discriminator;

  const relaxedOptions = def.options.map((option) => {
    if (typeNameOf(option) !== z.ZodFirstPartyTypeKind.ZodObject) {
      return option;
    }

    const optionDef = (option as unknown as z.ZodObject<z.ZodRawShape>)._def;
    const optionShape = optionDef.shape();
    const relaxedShape: z.ZodRawShape = {};
    for (const key of Object.keys(optionShape)) {
      relaxedShape[key] =
        key === discriminator ? optionShape[key] : relaxField(optionShape[key], depth + 1);
    }
    const relaxedOption = z.object(relaxedShape);
    // Same unknown-key treatment as `relaxObject`: `strict` relaxes to strip,
    // `passthrough` is preserved so an arm's extra keys still survive.
    return optionDef.unknownKeys === 'passthrough' ? relaxedOption.passthrough() : relaxedOption;
  });

  // Every arm must still be an object for zod to build the discriminator map.
  if (relaxedOptions.some((o) => typeNameOf(o) !== z.ZodFirstPartyTypeKind.ZodObject)) {
    return schema;
  }

  return z.discriminatedUnion(
    discriminator,
    relaxedOptions as unknown as [
      z.ZodDiscriminatedUnionOption<string>,
      z.ZodDiscriminatedUnionOption<string>,
      ...z.ZodDiscriminatedUnionOption<string>[],
    ],
  );
}

function relax(schema: z.ZodTypeAny, depth: number): z.ZodTypeAny {
  if (depth > MAX_DEPTH || !schema?._def) {
    return schema;
  }

  const typeName = typeNameOf(schema);

  switch (typeName) {
    case z.ZodFirstPartyTypeKind.ZodObject:
      return relaxObject(schema as z.ZodObject<z.ZodRawShape>, depth);

    case z.ZodFirstPartyTypeKind.ZodArray: {
      const def = schema._def as unknown as {
        type: z.ZodTypeAny;
        minLength: { value: number; message?: string } | null;
        maxLength: { value: number; message?: string } | null;
        exactLength: { value: number; message?: string } | null;
      };
      let rebuilt = z.array(relax(def.type, depth + 1));
      // Length constraints are cardinality, not nullability — keep them.
      if (def.exactLength) rebuilt = rebuilt.length(def.exactLength.value, def.exactLength.message);
      if (def.minLength) rebuilt = rebuilt.min(def.minLength.value, def.minLength.message);
      if (def.maxLength) rebuilt = rebuilt.max(def.maxLength.value, def.maxLength.message);
      return rebuilt;
    }

    case z.ZodFirstPartyTypeKind.ZodEffects: {
      // Where coercion lives (`z.preprocess`) and where the app's refinements
      // live (`nullableRefinedDate`). Spreading `_def` carries the effect over
      // verbatim; only the wrapped schema is widened.
      const def = schema._def as unknown as { schema: z.ZodTypeAny };
      return new z.ZodEffects({
        ...(schema._def as z.ZodEffectsDef),
        schema: relax(def.schema, depth + 1),
      });
    }

    case z.ZodFirstPartyTypeKind.ZodOptional: {
      const def = schema._def as unknown as { innerType: z.ZodTypeAny };
      return relax(def.innerType, depth + 1).optional();
    }

    case z.ZodFirstPartyTypeKind.ZodNullable: {
      const def = schema._def as unknown as { innerType: z.ZodTypeAny };
      return relax(def.innerType, depth + 1).nullable();
    }

    case z.ZodFirstPartyTypeKind.ZodDefault: {
      // Factory passed through, not called — see `relaxField`.
      const def = schema._def as unknown as {
        innerType: z.ZodTypeAny;
        defaultValue: () => unknown;
      };
      return relax(def.innerType, depth + 1).default(def.defaultValue);
    }

    case z.ZodFirstPartyTypeKind.ZodCatch: {
      const def = schema._def as unknown as {
        innerType: z.ZodTypeAny;
        catchValue: (ctx: { error: z.ZodError; input: unknown }) => unknown;
      };
      return relax(def.innerType, depth + 1).catch(def.catchValue);
    }

    case z.ZodFirstPartyTypeKind.ZodUnion: {
      const def = schema._def as unknown as { options: z.ZodTypeAny[] };
      return z.union(
        def.options.map((option) => relax(option, depth + 1)) as unknown as [
          z.ZodTypeAny,
          z.ZodTypeAny,
          ...z.ZodTypeAny[],
        ],
      );
    }

    case z.ZodFirstPartyTypeKind.ZodDiscriminatedUnion:
      return relaxDiscriminatedUnion(
        schema as z.ZodDiscriminatedUnion<string, z.ZodDiscriminatedUnionOption<string>[]>,
        depth,
      );

    case z.ZodFirstPartyTypeKind.ZodIntersection: {
      const def = schema._def as unknown as { left: z.ZodTypeAny; right: z.ZodTypeAny };
      return z.intersection(relax(def.left, depth + 1), relax(def.right, depth + 1));
    }

    case z.ZodFirstPartyTypeKind.ZodLazy: {
      // Lazy defers evaluation, which is what keeps a recursive schema from
      // recursing forever here: the getter only runs when a value is parsed, and
      // by then the node below is a fresh lazy wrapper rather than a recursion.
      //
      // But zod calls the getter on EVERY parse, so relaxing inside it naively
      // would rebuild that subtree per response. Cache the rebuilt inner schema
      // on first use — the top-level WeakMap can't cover this, since the work
      // happens after it has already returned.
      const def = schema._def as unknown as { getter: () => z.ZodTypeAny };
      let relaxedInner: z.ZodTypeAny | undefined;
      return z.lazy(() => {
        relaxedInner ??= relax(def.getter(), depth + 1);
        return relaxedInner;
      });
    }

    case z.ZodFirstPartyTypeKind.ZodRecord: {
      const def = schema._def as unknown as {
        keyType: z.ZodTypeAny;
        valueType: z.ZodTypeAny;
      };
      return z.record(
        def.keyType as z.ZodString,
        relaxField(def.valueType, depth + 1),
      ) as unknown as z.ZodTypeAny;
    }

    case z.ZodFirstPartyTypeKind.ZodTuple: {
      const def = schema._def as unknown as { items: z.ZodTypeAny[]; rest: z.ZodTypeAny | null };
      const rebuilt = z.tuple(
        def.items.map((item) => relaxField(item, depth + 1)) as unknown as [],
      );
      return def.rest ? rebuilt.rest(relax(def.rest, depth + 1)) : rebuilt;
    }

    case z.ZodFirstPartyTypeKind.ZodReadonly: {
      const def = schema._def as unknown as { innerType: z.ZodTypeAny };
      return relax(def.innerType, depth + 1).readonly();
    }

    case z.ZodFirstPartyTypeKind.ZodBranded: {
      const def = schema._def as unknown as { type: z.ZodTypeAny };
      return relax(def.type, depth + 1);
    }

    case z.ZodFirstPartyTypeKind.ZodPipeline: {
      const def = schema._def as unknown as { in: z.ZodTypeAny; out: z.ZodTypeAny };
      return z.pipeline(relax(def.in, depth + 1), relax(def.out, depth + 1));
    }

    // Everything below passes through untouched, for one of two reasons.
    //
    // Leaves (string/number/boolean/date/literal/enum/…): no nested schema and
    // no nullability of their own — the field wrapper is what widens them.
    //
    // Map/Set/Function/Promise: containers the read schemas do not currently
    // use. Left unrelaxed deliberately — NOT relaxing is always safe (it
    // degrades that subtree to strict), whereas rebuilding one wrong is not.
    // Give one its own case above if a read schema starts using it.
    case z.ZodFirstPartyTypeKind.ZodString:
    case z.ZodFirstPartyTypeKind.ZodNumber:
    case z.ZodFirstPartyTypeKind.ZodNaN:
    case z.ZodFirstPartyTypeKind.ZodBigInt:
    case z.ZodFirstPartyTypeKind.ZodBoolean:
    case z.ZodFirstPartyTypeKind.ZodDate:
    case z.ZodFirstPartyTypeKind.ZodSymbol:
    case z.ZodFirstPartyTypeKind.ZodUndefined:
    case z.ZodFirstPartyTypeKind.ZodNull:
    case z.ZodFirstPartyTypeKind.ZodAny:
    case z.ZodFirstPartyTypeKind.ZodUnknown:
    case z.ZodFirstPartyTypeKind.ZodNever:
    case z.ZodFirstPartyTypeKind.ZodVoid:
    case z.ZodFirstPartyTypeKind.ZodLiteral:
    case z.ZodFirstPartyTypeKind.ZodEnum:
    case z.ZodFirstPartyTypeKind.ZodNativeEnum:
    case z.ZodFirstPartyTypeKind.ZodMap:
    case z.ZodFirstPartyTypeKind.ZodSet:
    case z.ZodFirstPartyTypeKind.ZodFunction:
    case z.ZodFirstPartyTypeKind.ZodPromise:
    case undefined:
      return schema;
  }
}

/**
 * Return a variant of `schema` that accepts `null`/`undefined` wherever the
 * original required a value, with all coercions, transforms and refinements
 * still in place. Cached per schema object.
 */
export function relaxNullability<Schema extends z.ZodTypeAny>(schema: Schema): z.ZodTypeAny {
  const cached = cache.get(schema);
  if (cached) {
    return cached;
  }

  let relaxed: z.ZodTypeAny;
  try {
    relaxed = relax(schema, 0);
  } catch {
    // A schema shape we cannot rebuild must not take down the read path; fall
    // back to the strict schema so the caller drops to its last-resort tier.
    relaxed = schema;
  }

  cache.set(schema, relaxed);
  return relaxed;
}
